import type { Oklch } from './oklch'
import { oklchToHex, oklchToRgba } from './oklch'

/**
 * The card's colours, mirrored from `src/styles/tokens.css`.
 *
 * These are the OKLCH triples as authored, not resolved hex — the conversion
 * happens at render time through `oklch.ts`. Storing triples rather than hex is
 * what makes the drift test in `card.test.ts` possible: it parses `tokens.css`
 * and asserts every value below still matches its `--c-*` counterpart. Hard-coded
 * hex would need the same test to hold a second copy of the conversion, which is
 * the thing being tested.
 *
 * `src/brand.ts` already carries one hand-maintained OKLCH-to-sRGB pair
 * (`THEME_COLOR`, for the `theme-color` meta tag) and its own comment admits the
 * drift risk without guarding it. The test here closes that too, by asserting the
 * converter reproduces both of those literals exactly.
 *
 * Only the tokens the card actually paints are listed. Adding a colour to the
 * card means adding it here and the drift test will then hold it to `tokens.css`.
 */

export type CardTheme = 'dark' | 'light'

type TokenName =
  | 'bgBase'
  | 'bgSurface'
  | 'borderSubtle'
  | 'borderStrong'
  | 'textPrimary'
  | 'textSecondary'
  | 'textTertiary'
  | 'textMuted'
  | 'fan1'
  | 'fan3'
  | 'fanMedian'
  | 'fire'
  | 'fireBright'
  | 'confidenceLow'
  | 'confidenceHigh'

/**
 * The CSS custom property each token mirrors. The drift test reads this map, so
 * a rename in `tokens.css` fails the suite instead of silently painting a stale
 * colour onto every shared card.
 */
export const TOKEN_CSS_NAMES: Record<TokenName, string> = {
  bgBase: '--c-bg-base',
  bgSurface: '--c-bg-surface',
  borderSubtle: '--c-border-subtle',
  borderStrong: '--c-border-strong',
  textPrimary: '--c-text-primary',
  textSecondary: '--c-text-secondary',
  textTertiary: '--c-text-tertiary',
  textMuted: '--c-text-muted',
  fan1: '--c-fan-1',
  fan3: '--c-fan-3',
  fanMedian: '--c-fan-median',
  fire: '--c-fire',
  fireBright: '--c-fire-bright',
  confidenceLow: '--c-confidence-low',
  confidenceHigh: '--c-confidence-high',
}

export const CARD_TOKENS: Record<CardTheme, Record<TokenName, Oklch>> = {
  dark: {
    bgBase: [0.175, 0.014, 258],
    bgSurface: [0.215, 0.016, 258],
    borderSubtle: [0.275, 0.016, 258],
    borderStrong: [0.44, 0.022, 258],
    textPrimary: [0.975, 0.004, 258],
    textSecondary: [0.8, 0.012, 258],
    textTertiary: [0.645, 0.014, 258],
    textMuted: [0.52, 0.014, 258],
    fan1: [0.46, 0.062, 212],
    fan3: [0.675, 0.115, 200],
    fanMedian: [0.885, 0.145, 192],
    fire: [0.835, 0.155, 78],
    fireBright: [0.905, 0.15, 84],
    confidenceLow: [0.58, 0.022, 80],
    confidenceHigh: [0.875, 0.16, 80],
  },
  light: {
    bgBase: [0.985, 0.003, 258],
    bgSurface: [1, 0, 0],
    borderSubtle: [0.925, 0.008, 258],
    borderStrong: [0.755, 0.014, 258],
    textPrimary: [0.215, 0.018, 258],
    textSecondary: [0.4, 0.016, 258],
    textTertiary: [0.535, 0.014, 258],
    textMuted: [0.645, 0.012, 258],
    fan1: [0.74, 0.055, 205],
    fan3: [0.6, 0.098, 199],
    fanMedian: [0.46, 0.108, 194],
    fire: [0.66, 0.15, 62],
    fireBright: [0.72, 0.16, 66],
    confidenceLow: [0.6, 0.022, 62],
    confidenceHigh: [0.63, 0.155, 62],
  },
}

/**
 * The band gradient stops, copied from `FanChart.tsx`'s `<linearGradient>` pair.
 * SVG expresses these as `stop-opacity`; canvas has no such thing, so the alpha
 * is baked into each colour string by `cardPalette`.
 */
const BAND_ALPHA = {
  outerFrom: 0.22,
  outerTo: 0.62,
  innerFrom: 0.3,
  innerTo: 0.78,
} as const

/** Matches the SVG median glow: same colour as the line, at low alpha behind it. */
const MEDIAN_GLOW_ALPHA = 0.16

/** The requirement line's own opacity in the app. */
const REQUIREMENT_ALPHA = 0.9

/** The soft halo behind the crossing marker. The app pulses it; a card cannot. */
const MARKER_HALO_ALPHA = 0.22

export interface CardPalette {
  bg: string
  surface: string
  grid: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  textMuted: string
  /** p10–p90. Two stops, left to right across the plot. */
  bandOuter: readonly [string, string]
  /** p25–p75. */
  bandInner: readonly [string, string]
  medianLine: string
  medianGlow: string
  requirement: string
  /** The vertical rule at the target age. */
  targetRule: string
  /** The crossing marker: a soft halo, a solid dot, and a ring punched out of it. */
  markerHalo: string
  markerDot: string
  markerRing: string
}

export function cardPalette(theme: CardTheme): CardPalette {
  const t = CARD_TOKENS[theme]
  return {
    bg: oklchToHex(t.bgBase),
    surface: oklchToHex(t.bgSurface),
    grid: oklchToHex(t.borderSubtle),
    textPrimary: oklchToHex(t.textPrimary),
    textSecondary: oklchToHex(t.textSecondary),
    textTertiary: oklchToHex(t.textTertiary),
    textMuted: oklchToHex(t.textMuted),
    bandOuter: [
      oklchToRgba(t.fan1, BAND_ALPHA.outerFrom),
      oklchToRgba(t.fan1, BAND_ALPHA.outerTo),
    ],
    bandInner: [
      oklchToRgba(t.fan3, BAND_ALPHA.innerFrom),
      oklchToRgba(t.fan3, BAND_ALPHA.innerTo),
    ],
    medianLine: oklchToHex(t.fanMedian),
    medianGlow: oklchToRgba(t.fanMedian, MEDIAN_GLOW_ALPHA),
    requirement: oklchToRgba(t.fire, REQUIREMENT_ALPHA),
    targetRule: oklchToHex(t.borderStrong),
    markerHalo: oklchToRgba(t.fire, MARKER_HALO_ALPHA),
    markerDot: oklchToHex(t.fireBright),
    // The dot is ringed in the card's own background so it stays legible where it
    // sits on top of the median line.
    markerRing: oklchToHex(t.bgBase),
  }
}

/**
 * The headline probability's colour, at a given probability.
 *
 * The app writes this as `color-mix(in oklch, var(--c-confidence-low),
 * var(--c-confidence-high) N%)`. That is reproducible here without a browser
 * because the two endpoints **share a hue** — 80 on dark, 62 on light — so the
 * mix is a straight interpolation of lightness and chroma with nothing happening
 * to the hue angle at all. `tokens.css` fixes the hue deliberately (an earlier
 * cool-to-gold version travelled through green); that decision is what makes this
 * function exact rather than an approximation.
 *
 * If the two endpoints are ever given different hues, this silently becomes wrong
 * — hence the assertion in `card.test.ts` that they still match.
 */
export function confidenceColor(probability: number, theme: CardTheme): string {
  const t = CARD_TOKENS[theme]
  const [lowL, lowC, hue] = t.confidenceLow
  const [highL, highC] = t.confidenceHigh

  // The app rounds to whole percent before mixing, so the card must too or the
  // two disagree by a fraction of a step at the same probability.
  const mix = Math.round(Math.min(1, Math.max(0, probability)) * 100) / 100

  return oklchToHex([lowL + (highL - lowL) * mix, lowC + (highC - lowC) * mix, hue])
}
